<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied/>
  </div>
  <div v-else>
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li class="nav-item">
          <a class="nav-link active">
            {{ $t('All') }}
            <span class="badge rounded-pill bg-primary">{{ datatable.total }}</span>
          </a>
        </li>
      </ul>
      <column-toggle
        :headers="allHeaders"
        storage-key="deliveryPlatforms.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <div class="btn-group btn-group-sm me-sm-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="() => { offcanvasFroms.open = true; offcanvasFroms.id = 0; offcanvasFroms.type = 'CREATE'; }"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>
      </div>
      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button class="input-group-text" type="button" @click="fetchData">
          <i class="fas fa-search" />
        </button>
      </div>
      <per-page @per-page="setPerPage" />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      buttons-pagination
      :body-item-class-name="bodyItemClassNameFunction"
      table-class-name='customize-table'
    >
      <template #item-name="item">
        <span class="badge me-2" :style="{ backgroundColor: item.color || '#6B7280' }">
          <i v-if="item.icon" :class="item.icon" class="me-1"/>{{ item.name }}
        </span>
        <span class="text-muted small">{{ item.code }}</span>
      </template>
      <template #item-is_external="item">
        <span v-if="item.is_external" class="badge bg-warning text-dark">{{ $t('_delivery.External platform') }}</span>
        <span v-else class="badge bg-secondary">{{ $t('_delivery.In-house') }}</span>
      </template>
      <template #item-default_commission_rate="item">
        {{ item.is_external ? Number(item.default_commission_rate).toFixed(2) + '%' : '-' }}
      </template>
      <template #item-platform_product_count="item">
        <span v-if="item.is_external">{{ item.platform_product_count }} {{ $t('_delivery.items') }}</span>
        <span v-else>-</span>
      </template>
      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => { offcanvasFroms.open = true; offcanvasFroms.id = item.id; offcanvasFroms.type = 'EDIT'; }"
            >
              <i class="fa-solid fa-square-pen" />
            </button>
            <template #content><div>{{ $t('Edit') }}</div></template>
          </popper>
          <popper v-if="item.is_external" :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => { manageBranches.open = true; manageBranches.platform = item; }"
            >
              <i class="fa-solid fa-store" />
            </button>
            <template #content><div>{{ $t('_delivery.Manage branches') }}</div></template>
          </popper>
          <popper v-if="item.is_external" :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => { priceBook.open = true; priceBook.platform = item; }"
            >
              <i class="fa-solid fa-tags" />
            </button>
            <template #content><div>{{ $t('_delivery.Platform prices') }}</div></template>
          </popper>
          <popper v-if="item.is_external" :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => { settlement.open = true; settlement.platform = item; }"
            >
              <i class="fa-solid fa-hand-holding-dollar" />
            </button>
            <template #content><div>{{ $t('_delivery.Receive platform payout') }}</div></template>
          </popper>
          <popper :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
            <button type="button" class="btn btn-outline-primary btn-sm btn-icon" @click="handleDelete(item.id)">
              <i class="fa-solid fa-trash-can" />
            </button>
            <template #content><div>{{ $t('Delete') }}</div></template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>
    <offcanvas-form
      v-if="offcanvasFroms.open"
      :type="offcanvasFroms.type"
      :id="offcanvasFroms.id"
    />
    <offcanvas-manage-branches
      v-if="manageBranches.open"
      :platform="manageBranches.platform"
    />
    <offcanvas-price-book
      v-if="priceBook.open"
      :platform="priceBook.platform"
    />
    <offcanvas-settlement
      v-if="settlement.open"
      :platform="settlement.platform"
    />
  </div>
</template>
<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import OffcanvasManageBranches from './OffcanvasManageBranches.vue';
import OffcanvasPriceBook from './OffcanvasPriceBook.vue';
import OffcanvasSettlement from './OffcanvasSettlement.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import { DeliveryPlatforms } from '@/services/endpoint/delivery-platforms.service';
import Popper from "vue3-popper";

export default {
  data() {
    return {
      params: { limit: 10, page: 1, search: '' },
      pageLoading: false,
      hasPermission: false,
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: { page: 1, rowsPerPage: 10 }
      },
      offcanvasFroms: { open: false, type: 'CREATE', id: 0 },
      manageBranches: { open: false, platform: null },
      priceBook: { open: false, platform: null },
      settlement: { open: false, platform: null },
      visibleColumns: loadVisibleColumns('deliveryPlatforms.columns', FieldsDef(t => t))
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    PerPage,
    Vue3EasyDataTable,
    OffcanvasForm,
    OffcanvasManageBranches,
    OffcanvasPriceBook,
    OffcanvasSettlement,
    ColumnToggle,
    Popper
  },
  computed: {
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2';
      const columnEnd = ['created_by'];
      if (columnEnd.includes(column)) className += ' text-end';
      return className;
    },
    async fetchData() {
      this.datatable.loading = true;
      var queryString = '?' + $.param(this.params);
      await DeliveryPlatforms.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) this.hasPermission = false;
      });
      this.datatable.loading = false;
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    async handleDelete(id) {
      try {
        let self = this;
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: this.$t("_delivery.Delete this delivery channel") + '?',
          // ออเดอร์เก่ายังอ้างช่องทางนี้ในรายงานได้ แค่หายจากตัวเลือกตอนคีย์
          html: this.$t("_delivery.Past orders keep their history - the channel disappears from POS and settings"),
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm"),
          buttonsStyling: false,
          customClass: { confirmButton: "btn btn-primary me-2", cancelButton: "btn btn-danger" },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return DeliveryPlatforms.delete(id)
              .then(() => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
              })
              .catch((error) => {
                if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(self.$t(error?.response?.data?.message))
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {}
    },
  },
  watch: {
    "datatable.serverOptions" (nval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
